import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Dropdown, Input, Select, Table, Tooltip } from 'antd';
import type { MenuProps, TableProps } from 'antd';
import { ListChecks, MoreHorizontal } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { POST_TYPE_SORT_FIELDS, type Paginated, type PostTypeDto, type PostTypeSortField } from '@pams/shared';
import { EmptyState } from '@/components/EmptyState';
import { FilterField } from '@/components/FilterField';
import { NameCell } from '@/components/NameCell';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { useAuth } from '@/hooks/useAuth';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import { columnKeySet } from '@/lib/tableColumns';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { useServerTable } from '@/hooks/useServerTable';
import { can, READ_ONLY_TOOLTIP } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { templateDecisionHref } from '@/lib/templateLinks';
import { PostTypeDrawer } from './components/PostTypeDrawer';
import { postTypePrefillOf, type PostTypePrefill } from './prefill';

/** How many eligible ranks fit in a cell before it becomes "+N more". */
const MAX_VISIBLE_RANKS = 4;

/**
 * The post type master (FR-5.1.3) — a GLOBAL master owned by no org node.
 *
 * `useServerTable`'s fifth consumer, again with no change to the hook.
 */
const PostTypesPage = () => {
  const { officer } = useAuth();
  const queryClient = useQueryClient();
  /**
   * ⚠️ `modal` FROM `App.useApp()`, NOT THE STATIC `Modal.confirm`.
   *
   * Two reasons, and the second was measured. CLAUDE.md §8 already records that AntD
   * statics always use the ROOT theme and escape a `ThemeScope`. And the static does not
   * render AT ALL under jsdom — which is why no confirmation dialog in this repository
   * had a test before P2. The hook form renders, is themed, and is assertable.
   */
  const { message, modal } = App.useApp();
  const canWrite = officer ? can(officer.accessRole, 'postType.write') : false;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  /*
   * [P7B.1 §3b] A link from a column decision opens the create drawer prefilled — read ONCE, and for a
   * writer only: the drawer is an offer, and somebody who cannot save gets the list as it always was.
   */
  const [editing, setEditing] = useState<
    { mode: 'create' | 'edit'; postType?: PostTypeDto; prefill?: PostTypePrefill } | undefined
  >(() => {
    const prefill = postTypePrefillOf(searchParams);
    return prefill && canWrite ? { mode: 'create', prefill } : undefined;
  });
  /* Set once a save sends the officer back to the decisions, so closing does not navigate a second time. */
  const leaving = useRef(false);

  const table = useServerTable<PostTypeDto, PostTypeSortField>({
    // The SAME tuple the API feeds to `createPaginationSchema`. One allowlist.
    sortFields: POST_TYPE_SORT_FIELDS,
    defaultSortBy: 'displayOrder',
    defaultSortOrder: 'asc',
    filterKeys: ['show'],
    queryKey: (params) => queryKeys.postTypes.list(params),
    queryFn: (params, signal) =>
      api.get<Paginated<PostTypeDto>>('/post-types', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
          ...params.filters,
        },
        signal,
      }),
  });

  /*
   * ⚠️ `|| 'current'` — `useServerTable` STRIPS an empty filter value so it never reaches
   * the URL, which is what keeps a shared link clean. The page therefore re-defaults on the
   * way out, exactly as `pages/Arrangements` does for `kind`.
   */
  const show = table.params.filters.show || 'current';

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.postTypes.all });
    // A policy version's embedded post-type summaries change with the master.
    void queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });
  };

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post<PostTypeDto>(`/post-types/${id}/${active ? 'restore' : 'archive'}`),
    onSuccess: (_data, variables) => {
      void message.success(variables.active ? 'Post type restored' : 'Post type archived');
      invalidate();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const confirmArchive = (postType: PostTypeDto) =>
    modal.confirm({
      title: `Archive ${postType.name}?`,
      content:
        'Archiving is used instead of deletion, because published policy versions reference this post type for ever. ' +
        'It stops appearing in NEW policy versions only; existing ones keep rendering it.',
      okText: 'Archive',
      okButtonProps: { danger: true },
      onOk: () => void setActive.mutateAsync({ id: postType.id, active: false }),
    });

  const rowActions = (postType: PostTypeDto): MenuProps['items'] =>
    postType.archivedAt === null
      ? [
          {
            key: 'archive',
            label: 'Archive',
            danger: true,
            disabled: !canWrite,
            onClick: () => void confirmArchive(postType),
          },
        ]
      : [
          {
            key: 'restore',
            label: 'Restore',
            disabled: !canWrite,
            onClick: () => void setActive.mutateAsync({ id: postType.id, active: true }),
          },
        ];

  const allColumns: TableProps<PostTypeDto>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      // The first column is the way in: it OPENS the row (read-only without the write
      // capability), while `Edit` beside it stays the write action. See `NameCell`.
      render: (name: string, row) => (
        <NameCell
          name={name}
          onOpen={() => setEditing({ mode: 'edit', postType: row })}
          // The short name as a muted second line — the alias-cell idiom from /design.
          secondary={row.shortName}
        />
      ),
    },
    {
      title: 'Eligible ranks',
      key: 'eligibleRanks',
      render: (_value, row) => {
        if (row.eligibleRanks.length === 0) return <span className="text-subtle">—</span>;
        const shown = row.eligibleRanks.slice(0, MAX_VISIBLE_RANKS);
        const rest = row.eligibleRanks.length - shown.length;
        return (
          <span className="text-sm" title={row.eligibleRanks.map((rank) => rank.name).join(', ')}>
            {shown.map((rank) => rank.shortName).join(' · ')}
            {rest > 0 && <span className="text-muted-foreground"> +{rest} more</span>}
          </span>
        );
      },
    },
    {
      title: 'Order',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      sorter: true,
      align: 'right',
      width: 90,
      render: (order: number) => <span className="tabular-nums">{order}</span>,
    },
    {
      title: 'Status',
      /*
       * ⚠️ [LIFECYCLE] `dataIndex` MOVED WITH THE COLUMN, and it had to. It read `isActive`,
       * which no longer exists on the DTO — so `render` would have received `undefined` and
       * drawn EVERY row as archived, with no type error, because `dataIndex` is a string.
       *
       * The `key` moves too, from `isActive` to `lifecycle`. `useColumnVisibility` stores the
       * HIDDEN set by key, so a stored preference naming the old key simply stops matching
       * and the column becomes visible again — the safe direction, and the reason the hook
       * keys on hidden rather than on shown.
       */
      dataIndex: 'archivedAt',
      key: 'lifecycle',
      width: 120,
      // A DOT, not a filled badge — a column of pills is a wall of colour.
      render: (archivedAt: string | null) =>
        archivedAt === null ? (
          <StatusDot tone="success" label="Active" />
        ) : (
          <StatusDot tone="neutral" label="Archived" />
        ),
    },
    {
      title: '',
      key: 'actions',
      // [P4-UX] Pinned: an action a scroll can hide is an action that is not there.
      fixed: 'right' as const,
      align: 'right',
      width: 120,
      render: (_value, row) => (
        <div className="flex items-center justify-end gap-1">
          <Tooltip title={canWrite ? undefined : READ_ONLY_TOOLTIP}>
            <Button
              type="text"
              size="small"
              disabled={!canWrite}
              onClick={() => setEditing({ mode: 'edit', postType: row })}
            >
              Edit
            </Button>
          </Tooltip>
          {/* The overflow never reuses the label "Edit" — duplicate accessible names
              break row-level queries. */}
          <Dropdown menu={{ items: rowActions(row) }} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" aria-label={`More actions for ${row.name}`}>
              <MoreHorizontal className="size-4" aria-hidden />
            </Button>
          </Dropdown>
        </div>
      ),
    },
  ];

  /*
   * ⚠️ THE MENU GETS THE UNFILTERED ARRAY; THE TABLE GETS THE FILTERED ONE. That is the
   * circularity solved — by never letting the two be the same variable. Keys and locks are
   * DERIVED from the array above, so this page keeps no second list of columns.
   */
  const columnKeys = columnKeySet(allColumns ?? []);
  const columnVisibility = useColumnVisibility({ ...columnKeys, storageKey: 'post-types' });
  const columns = (allColumns ?? []).filter((column) => columnVisibility.isVisible(String(column.key)));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={ListChecks}
        title="Post types"
        subtitle={
          <>
            {table.total} — the columns a policy can be built from.{' '}
            {/*
              Stated on the LIST as well as in the editor, because the question "should
              Supervision be in here?" is asked while looking at this table, before
              anybody opens the drawer that answers it.
            */}
            <span className="text-subtle">
              Command roles are not post types — they live on each policy and are never counted.
            </span>
          </>
        }
        actions={
          <Tooltip title={canWrite ? undefined : READ_ONLY_TOOLTIP}>
            <Button type="primary" disabled={!canWrite} onClick={() => setEditing({ mode: 'create' })}>
              New post type
            </Button>
          </Tooltip>
        }
        filters={
          <>
            {/*
              ⚠️ [LIFECYCLE] TWO AXES, TWO CONTROLS — never one mixed dropdown.

              `Show` says whether a row is in circulation; a status filter (where one exists)
              says where it is in its own life. Collapsing them into one list would make
              "Archived" look like a peer of "Draft", which is exactly the confusion the
              archive-is-an-axis design exists to prevent.

              ⚠️ A `Select`, NOT a `Segmented`. `Segmented` is this repository's TWO-value
              switch and `pages/Arrangements` says so in prose; three values stop it being a
              switch, and on `/arrangements` it would sit beside the kind Segmented and read
              as a second tab bar.

              ⚠️ NO `allowClear`. "Current" is a VALUE, not the absence of a filter — clearing
              would emit `undefined` and the page would have to re-default it.
            */}
            <FilterField label="Show" className="w-44">
              <Select
                className="w-full"
                value={show}
                onChange={(value: string) => table.setFilter('show', value === 'current' ? undefined : value)}
                options={[
                  { value: 'current', label: 'Current' },
                  { value: 'archived', label: 'Archived' },
                  { value: 'all', label: 'All' },
                ]}
                data-testid="show-filter"
              />
            </FilterField>
            <FilterField label="Search" className="w-64">
              <Input.Search
                allowClear
                placeholder="Name"
                value={table.searchInput}
                onChange={(event) => table.onSearchChange(event.target.value)}
                data-testid="post-type-search"
              />
            </FilterField>
            {/* The Columns menu sits IN the filter row, not in `actions`: it is a view control,
                the same argument `pages/Arrangements` makes for its Segmented. */}
            <ColumnsMenu
              className="ml-auto"
              columns={allColumns ?? []}
              visibility={columnVisibility}
              locked={columnKeys.locked}
            />
          </>
        }
      />

      <Table<PostTypeDto>
        {...table.tableProps}
        columns={columns}
        size="middle"
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <EmptyState
              size="sm"
              title={table.searchInput ? 'No post types match this search.' : 'No post types yet.'}
            />
          ),
        }}
      />

      {editing && (
        <PostTypeDrawer
          open
          mode={editing.mode}
          {...(editing.postType ? { postType: editing.postType } : {})}
          {...(editing.prefill ? { prefill: editing.prefill } : {})}
          readOnly={!canWrite}
          onClose={() => {
            setEditing(undefined);
            /* The link's words leave the URL, so a reload does not open the drawer again. */
            if (editing.prefill && !leaving.current) void navigate({ search: '' }, { replace: true });
          }}
          onSuccess={() => {
            invalidate();
            const back = editing.prefill?.templates;
            if (!back) return;
            /* The new post type may now match the column by rule — the set's decisions and readiness move. */
            leaving.current = true;
            void queryClient.invalidateQueries({ queryKey: queryKeys.templates.set(back.setKey) });
            void navigate(templateDecisionHref(back.setKey, 'FORCE_COLUMN', back.column));
          }}
        />
      )}
    </div>
  );
};

export default PostTypesPage;
